import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ORGANIZER_INVITATION_ROUTE, SELECT_VENUE_ROUTE } from '@constants/routes';
import { useAppLaunchDetection } from '@hooks/use-app-launch-detection';
import { getLastPathCookie } from '@utils/last-path-storage/get-last-path-cookie';

/**
 * Restores the last visited path on every full page load in the Xceed Pro webview.
 */
export const LastPathPersistence: React.FC = () => {
  const { isInitialLoad, resetInitialLoad } = useAppLaunchDetection();
  const location = useLocation();

  useLayoutEffect(() => {
    // Only apply during initial load (any path, not just root paths)
    if (isInitialLoad) {
      // Add a small delay to ensure any pending localStorage operations are complete
      setTimeout(() => {
        // Get the last visited path from localStorage
        const lastPath = getLastPathCookie();

        // Reset the initial load state to prevent multiple redirects
        resetInitialLoad();

        // Decide on the pathname but replace() with the full stored value: comparing the full path
        // loops when a route normalizes its query on mount, and it also drops a launch-time query on a
        // deep link. Matching pathnames after the reload guarantees termination whatever the query does.
        const storedPathname = lastPath?.split('?')[0];
        // An invite link opened in the app is where the user meant to go, not a cold launch to resume from.
        const isRestorable = location.pathname !== ORGANIZER_INVITATION_ROUTE;
        if (
          isRestorable &&
          lastPath &&
          storedPathname !== location.pathname &&
          lastPath !== '/' &&
          lastPath !== SELECT_VENUE_ROUTE
        ) {
          // Use replace to completely bypass the router
          // This prevents any router redirects from happening
          globalThis.location.replace(lastPath);
        }
      }, 50); // Small delay to ensure localStorage operations are complete
    }
  }, [isInitialLoad, location.pathname, location.search, resetInitialLoad]);

  // This component doesn't render anything
  return null;
};
